/* eslint-disable react/no-array-index-key */
/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import 'react-dates/initialize';
import { DayPickerSingleDateController } from 'react-dates';
import moment from 'moment';
import { ArrowBack, ArrowForward } from '@carvana/icons';
import {
  SelectTimeSlotDate,
  SelectTimeSlotTime
} from '../../../../ducks/actions';
import TimeSlotButton from './time-slot-button';
import TimeWindowButton from './time-window-button';
import * as sc from './date-time-picker.styles';
import '@carvana/showroom-css-theme/Theme.css';
import '@carvana/showroom-carousel/index.css';
import { useSelector } from 'react-redux';

const DATE_FORMAT = 'MM/DD/YYYY';

const DateTimePicker = props => {
  const {
    rescheduleModal: {
      preferredTimeslotIndex,
      timeSlots,
      timeWindows,
      isFlexibleDelivery,
      timeZone,
      transferType
    }
  } = props;
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableTimes, setAvailableTimes] = useState([]);
  const [showCalendar, setShowCalendar] = useState(true);
  const [currentMonth, setCurrentMonth] = useState();
  const [disabledArrows, setDisabledArrows] = useState({});
  const firstAvailableDate = isFlexibleDelivery
    ? timeWindows[0]?.date
    : timeSlots[0]?.date;
  const lastAvailableDate = isFlexibleDelivery
    ? timeWindows[timeWindows.length - 1]?.date
    : timeSlots[timeSlots.length - 1]?.date;

  const isSameDay = (a, b) => {
    if (!moment.isMoment(a) || !moment.isMoment(b)) return false;

    return (
      a.date() === b.date() && a.month() === b.month() && a.year() === b.year()
    );
  };

  const isDesktop = useSelector(state => state.ui.screenSize.width >= 1024);

  const handleDateChange = selectedDay => {
    if (selectedDay) {
      setSelectedDate(selectedDay?.format(DATE_FORMAT) || '');
    }

    if (isFlexibleDelivery) {
      const selectedTimeWindow = timeWindows.find(timeWindow =>
        isSameDay(selectedDay, moment(timeWindow.date))
      );
      const { date, times } = selectedTimeWindow;
      setSelectedTime('');
      setAvailableTimes(times);
      SelectTimeSlotDate(date);
    } else {
      const selectedTimeSlot = timeSlots.find(timeSlot =>
        isSameDay(selectedDay, moment(timeSlot.date))
      );
      const { date, times } = selectedTimeSlot;

      setSelectedTime('');
      setAvailableTimes(times);
      SelectTimeSlotDate(date);
    }
  };

  const handleTimeChange = (selectedIndex, timeSelected) => {
    const { ...payload } = availableTimes[selectedIndex];
    setSelectedTime(timeSelected);
    SelectTimeSlotTime(payload);
  };

  const setArrowsDisplayProperty = month => {
    const disabled = {};
    if (moment(lastAvailableDate).isSame(month, 'month')) {
      disabled.arrowForward = true;
    } else {
      disabled.arrowForward = false;
    }

    if (moment(firstAvailableDate).isSame(month, 'month')) {
      disabled.arrowBackward = true;
    } else {
      disabled.arrowBackward = false;
    }

    setDisabledArrows(disabled);
  };

  useEffect(() => {
    if (preferredTimeslotIndex > -1) {
      setTimeout(() => {
        const preferredDate = isFlexibleDelivery
          ? timeWindows[preferredTimeslotIndex]?.date
          : timeSlots[preferredTimeslotIndex]?.date;
        handleDateChange(moment(preferredDate));
      }, 0);
    } else if (timeSlots.length !== 0) {
      handleDateChange(moment(firstAvailableDate));
    }
    const firstAvailableMonth = !firstAvailableDate
      ? moment()
      : moment(firstAvailableDate);
    setCurrentMonth(firstAvailableMonth);
    setArrowsDisplayProperty(firstAvailableMonth);
  }, [preferredTimeslotIndex]);

  return (
    <sc.TimeSlotWrapper>
      {!showCalendar && <sc.Titles>Selected Date - {selectedDate}</sc.Titles>}
      {showCalendar && (
        <sc.DayPickerWrapper>
          <sc.Titles>Select Date</sc.Titles>
          <DayPickerSingleDateController
            focused
            hideKeyboardShortcutsPanel
            isDayBlocked={day1 =>
              isFlexibleDelivery
                ? !timeWindows.some(timeWindow =>
                    isSameDay(day1, moment(timeWindow.date))
                  )
                : !timeSlots.some(timeSlot =>
                    isSameDay(day1, moment(timeSlot.date))
                  )
            }
            initialVisibleMonth={() =>
              !firstAvailableDate ? moment() : moment(firstAvailableDate)
            }
            numberOfMonths={1}
            onDateChange={day => handleDateChange(day)}
            onNextMonthClick={month => {
              setCurrentMonth(month);
              setArrowsDisplayProperty(month);
            }}
            onPrevMonthClick={month => {
              setCurrentMonth(month);
              setArrowsDisplayProperty(month);
            }}
            date={!selectedDate ? null : moment(selectedDate, DATE_FORMAT)}
            daySize={40}
            navNext={
              <ArrowForward
                display={disabledArrows.arrowForward ? 'none' : ''}
                onClick={e => {
                  if (moment(lastAvailableDate).isSame(currentMonth, 'month'))
                    e.stopPropagation();
                }}
              />
            }
            navPrev={
              <ArrowBack
                display={disabledArrows.arrowBackward ? 'none' : ''}
                onClick={e => {
                  if (moment(firstAvailableDate).isSame(currentMonth, 'month'))
                    e.stopPropagation();
                }}
              />
            }
          />
        </sc.DayPickerWrapper>
      )}

      <sc.HideCalendar onClick={() => setShowCalendar(!showCalendar)}>
        {showCalendar ? 'Hide' : 'Show'} full calendar{' '}
        <sc.DownArrow rotate={showCalendar ? 'true' : null} />
      </sc.HideCalendar>

      <sc.VerticalDivider />

      {isFlexibleDelivery ? (
        <sc.TimeWrapper>
          <sc.TitlesWindow>
            Select Time Window&nbsp;
            <sc.TimeZoneTitle>&#40;{timeZone}&#41;</sc.TimeZoneTitle>
          </sc.TitlesWindow>
          <sc.SelectTimeWindowWrapper>
            {availableTimes.map((window, key) => (
              <TimeWindowButton
                ghost={window.time !== selectedTime}
                onClick={() => handleTimeChange(key, window.time)}
                selected={window.time === selectedTime}
              >
                {window.time} - {window.endRange}
              </TimeWindowButton>
            ))}
          </sc.SelectTimeWindowWrapper>
        </sc.TimeWrapper>
      ) : (
        <sc.TimeWrapper>
          <sc.Titles>
            Select Time
            <sc.TimeZoneTitle>&nbsp;&#40;{timeZone}&#41;</sc.TimeZoneTitle>
          </sc.Titles>
          <sc.SelectTimeWrapper>
            {isDesktop && (
              <sc.StyledCarousel enableDrag={false}>
                {availableTimes.map((slot, key) => (
                  <TimeSlotButton
                    ghost={slot.time !== selectedTime}
                    key={key}
                    onClick={() => handleTimeChange(key, slot.time)}
                    selected={slot.time === selectedTime}
                  >
                    {slot.time}
                  </TimeSlotButton>
                ))}
              </sc.StyledCarousel>
            )}
            {!isDesktop &&
              availableTimes.map((slot, key) => (
                <TimeSlotButton
                  ghost={slot.time !== selectedTime}
                  key={key}
                  onClick={() => handleTimeChange(key, slot.time)}
                  selected={slot.time === selectedTime}
                >
                  {slot.time}
                </TimeSlotButton>
              ))}
          </sc.SelectTimeWrapper>
        </sc.TimeWrapper>
      )}
    </sc.TimeSlotWrapper>
  );
};

DateTimePicker.propTypes = {
  rescheduleModal: PropTypes.shape({}).isRequired
};

DateTimePicker.defaultProps = {};

export default DateTimePicker;
